import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Pencil, X, MoreVertical, Settings2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useCounters } from "../../contexts/CountersContext";

const fmtEur = (cents) => {
    const n = Math.round(Number(cents) || 0);
    const sign = n < 0 ? "-" : "";
    const abs = Math.abs(n);
    const euros = Math.floor(abs / 100);
    const rem = abs % 100;
    // Italian thousands: dot every 3 digits
    const eurosStr = String(euros).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${sign}${eurosStr},${String(rem).padStart(2, "0")} €`;
};
const fmtDate = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    const dt = new Date(+y, +m - 1, +d);
    return dt.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const monthBounds = () => {
    const d = new Date();
    const f = new Date(d.getFullYear(), d.getMonth(), 1);
    const t = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const iso = (x) => x.toISOString().slice(0, 10);
    return [iso(f), iso(t)];
};

function KebabMenu({ item, onEdit, onDelete }) {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open]);
    const pick = (fn) => (e) => { e.stopPropagation(); setOpen(false); fn(); };
    return (
        <div className="lt-kebab-wrap" data-testid={`spesa-actions-${item.id}`}>
            <button type="button" className="lt-icon-btn" onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}>
                <MoreVertical size={18} />
            </button>
            {open && (
                <div className="lt-kebab-menu" onMouseLeave={() => setOpen(false)}>
                    <button className="lt-kebab-item" onClick={pick(onEdit)}><Pencil size={14} /> <span>Modifica</span></button>
                    <button className="lt-kebab-item lt-kebab-item--danger" onClick={pick(onDelete)}><Trash2 size={14} /> <span>Elimina</span></button>
                </div>
            )}
        </div>
    );
}

function SpesaForm({ initial, categories, clients, onSave, onClose, saving }) {
    const isEdit = Boolean(initial?.id);
    const [desc, setDesc] = useState(initial?.description || "");
    const [amount, setAmount] = useState(initial ? (initial.amount_cents / 100).toString().replace(".", ",") : "");
    const [catId, setCatId] = useState(initial?.category?.id || "");
    const [date, setDate] = useState(initial?.date || todayISO());
    const [clientId, setClientId] = useState(initial?.client?.id || "");
    const [notes, setNotes] = useState(initial?.notes || "");

    const submit = (e) => {
        e.preventDefault();
        if (!desc.trim() || !amount.trim() || !catId || !date) {
            toast.error("Compila tutti i campi obbligatori"); return;
        }
        const payload = {
            description: desc.trim(),
            amount_eur: amount.trim(),
            category_id: catId, date,
            client_id: clientId || null,
        };
        if (notes.trim()) payload.notes = notes.trim();
        onSave(payload);
    };

    return (
        <form onSubmit={submit} className="lt-scad-form" data-testid="spesa-form">
            <div className="lt-form-row">
                <label className="lt-label">Descrizione *</label>
                <input className="lt-input" value={desc} onChange={(e) => setDesc(e.target.value)} required minLength={2} maxLength={300} data-testid="spesa-form-desc" />
            </div>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="lt-form-row">
                    <label className="lt-label">Importo (€) *</label>
                    <input className="lt-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="1.250,00" required data-testid="spesa-form-amount" />
                </div>
                <div className="lt-form-row">
                    <label className="lt-label">Data *</label>
                    <input type="date" className="lt-input" value={date} onChange={(e) => setDate(e.target.value)} required data-testid="spesa-form-date" />
                </div>
            </div>
            <div className="lt-form-row">
                <label className="lt-label">Categoria *</label>
                <select className="lt-select" value={catId} onChange={(e) => setCatId(e.target.value)} required data-testid="spesa-form-cat">
                    <option value="">Seleziona…</option>
                    {categories.filter(c => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="lt-toggle__hint" style={{ marginTop: 4 }}>Le categorie sono gestite da un amministratore.</p>
            </div>
            <div className="lt-form-row">
                <label className="lt-label">Cliente</label>
                <select className="lt-select" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="spesa-form-client">
                    <option value="">— Nessuno (spesa generale) —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
            </div>
            <div className="lt-form-row">
                <label className="lt-label">Note</label>
                <textarea className="lt-textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
            </div>
            <div className="lt-form-actions">
                <div style={{ flex: 1 }} />
                <button type="button" className="lt-btn-secondary" onClick={onClose}>Annulla</button>
                <button type="submit" className="lt-btn-primary" disabled={saving} data-testid="spesa-form-save">{isEdit ? "Salva" : "Aggiungi spesa"}</button>
            </div>
        </form>
    );
}

function CategoriesModal({ categories, onClose, onChange }) {
    const [newName, setNewName] = useState("");
    const [editing, setEditing] = useState({});

    const add = async () => {
        if (!newName.trim()) return;
        try { await api.post("/internal/spese-categorie", { name: newName.trim() }); setNewName(""); onChange(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };
    const rename = async (id, name) => {
        try { await api.patch(`/internal/spese-categorie/${id}`, { name: name.trim() }); setEditing({}); onChange(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };
    const toggle = async (id, active) => {
        try { await api.patch(`/internal/spese-categorie/${id}`, { active }); onChange(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };
    const del = async (id) => {
        try { await api.delete(`/internal/spese-categorie/${id}`); onChange(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Impossibile eliminare"); }
    };

    return (
        <div className="lt-modal-backdrop" onClick={onClose}>
            <div className="lt-modal" onClick={(e) => e.stopPropagation()} data-testid="spesa-cat-modal">
                <div className="lt-modal__head">
                    <h2 className="lt-modal__title">Gestisci categorie spese</h2>
                    <button className="lt-icon-btn" onClick={onClose}><X size={16} /></button>
                </div>
                <div className="lt-scad-form">
                    <div className="lt-form-row" style={{ display: "flex", gap: 8 }}>
                        <input className="lt-input" placeholder="Nuova categoria…" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} data-testid="spesa-cat-new" />
                        <button className="lt-icon-btn" onClick={add} title="Aggiungi categoria" data-testid="spesa-cat-add"><Plus size={16} /></button>
                    </div>
                    {categories.length === 0 ? (
                        <p className="lt-sub" style={{ padding: 12 }}>Nessuna categoria. Creane una per iniziare a registrare spese.</p>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                            {categories.map((c) => {
                                const canDelete = !c.active && !c.in_use;
                                const tip = c.active ? "Disattiva prima di eliminare" : (c.in_use ? "Categoria in uso" : "Elimina");
                                return (
                                    <div key={c.id} className="lt-item-row" style={{ borderTop: "1px solid #F2F3EF", opacity: c.active ? 1 : 0.6 }} data-testid={`spesa-cat-row-${c.id}`}>
                                        {editing.id === c.id ? (
                                            <>
                                                <input className="lt-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} autoFocus onKeyDown={(e) => { if (e.key === "Enter") rename(c.id, editing.name); }} />
                                                <button className="lt-btn-primary" onClick={() => rename(c.id, editing.name)}>Salva</button>
                                                <button className="lt-icon-btn" onClick={() => setEditing({})}><X size={14} /></button>
                                            </>
                                        ) : (
                                            <>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ color: "#181B20", fontWeight: 500 }}>{c.name}</div>
                                                    <div style={{ fontSize: 12, color: "#8A8F88" }}>{c.spese_count} spese · {c.total_amount_eur}</div>
                                                </div>
                                                <label className="lt-toggle" title={c.active ? "Attiva" : "Inattiva"}>
                                                    <input type="checkbox" checked={c.active} onChange={(e) => toggle(c.id, e.target.checked)} data-testid={`spesa-cat-toggle-${c.id}`} />
                                                    <span className="lt-toggle__track"><span className="lt-toggle__thumb" /></span>
                                                </label>
                                                <button className="lt-icon-btn" onClick={() => setEditing({ id: c.id, name: c.name })} title="Rinomina"><Pencil size={14} /></button>
                                                <button className="lt-icon-btn" onClick={() => canDelete && del(c.id)} disabled={!canDelete} title={tip} style={{ opacity: canDelete ? 1 : 0.35, cursor: canDelete ? "pointer" : "not-allowed" }}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function Spese() {
    const { counters, refresh: refreshCounters } = useCounters();
    const sc = counters?.spese || {};
    const [items, setItems] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [summary, setSummary] = useState(null);
    const [clients, setClients] = useState([]);
    const [cats, setCats] = useState([]);
    const [q, setQ] = useState("");
    const [catF, setCatF] = useState("");
    const [clientF, setClientF] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [editing, setEditing] = useState(null);
    const [confirmDel, setConfirmDel] = useState(null);
    const [showCats, setShowCats] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const [f, t] = monthBounds();
        setFromDate(f); setToDate(t);
    }, []);

    const load = useCallback(async () => {
        try {
            const params = { page: 1, page_size: 100 };
            if (q.trim()) params.q = q.trim();
            if (catF) params.category_id = catF;
            if (clientF) params.client_id = clientF;
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;
            const { data } = await api.get("/internal/spese", { params });
            setItems(data.items);
            setTotalAmount(data.total_amount_cents);
        } catch { toast.error("Errore caricamento spese"); }
    }, [q, catF, clientF, fromDate, toDate]);

    const loadSummary = useCallback(async () => {
        try {
            const params = { group_by: "category" };
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;
            const { data } = await api.get("/internal/spese/summary", { params });
            setSummary(data);
        } catch {}
    }, [fromDate, toDate]);

    const loadCats = useCallback(async () => {
        try { const { data } = await api.get("/internal/spese-categorie", { params: { active: "all" } }); setCats(data.items || []); }
        catch {}
    }, []);

    useEffect(() => {
        api.get("/internal/clients", { params: { status: "active", page_size: 200 } }).then(({ data }) => setClients(data.items || data));
        loadCats();
    }, [loadCats]);
    useEffect(() => { load(); loadSummary(); }, [load, loadSummary]);

    const create = async (payload) => {
        setSaving(true);
        try { await api.post("/internal/spese", payload); toast.success("Spesa aggiunta"); setShowNew(false); load(); loadSummary(); loadCats(); refreshCounters(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
    };
    const saveEdit = async (payload) => {
        setSaving(true);
        try { await api.patch(`/internal/spese/${editing.id}`, payload); toast.success("Spesa aggiornata"); setEditing(null); load(); loadSummary(); refreshCounters(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
    };
    const doDelete = async () => {
        try { await api.delete(`/internal/spese/${confirmDel.id}`); toast.success("Spesa eliminata"); setConfirmDel(null); load(); loadSummary(); loadCats(); refreshCounters(); }
        catch { toast.error("Errore"); }
    };

    const applyPreset = (preset) => {
        const d = new Date();
        let f, t;
        if (preset === "month") {
            f = new Date(d.getFullYear(), d.getMonth(), 1); t = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        } else if (preset === "prev_month") {
            f = new Date(d.getFullYear(), d.getMonth() - 1, 1); t = new Date(d.getFullYear(), d.getMonth(), 0);
        } else if (preset === "year") {
            f = new Date(d.getFullYear(), 0, 1); t = new Date(d.getFullYear(), 11, 31);
        } else if (preset === "12m") {
            f = new Date(d); f.setDate(f.getDate() - 365); t = d;
        }
        const iso = (x) => x.toISOString().slice(0, 10);
        setFromDate(iso(f)); setToDate(iso(t));
    };

    const counterCards = useMemo(() => [
        { key: "month", label: "Totale mese corrente", value: sc.spese_this_month_eur || fmtEur(0) },
        { key: "year",  label: "Totale anno corrente", value: sc.spese_this_year_eur || fmtEur(0) },
        { key: "count", label: "Numero totale spese",  value: sc.totali ?? 0 },
        { key: "cats",  label: "Categorie attive",     value: sc.categorie_attive ?? 0 },
    ], [sc]);

    const summaryMax = useMemo(() => {
        if (!summary?.by?.length) return 0;
        return Math.max(...summary.by.map(x => x.amount_cents));
    }, [summary]);

    return (
        <div style={{ padding: "32px 40px" }} data-testid="spese-page">
            <div className="flex items-center" style={{ marginBottom: 20 }}>
                <h1 className="lt-h1" style={{ flex: 1 }}>Spese e costi</h1>
                <button className="lt-btn-secondary" onClick={() => setShowCats(true)} data-testid="spese-manage-cats" style={{ marginRight: 8 }}><Settings2 size={14} /> Gestisci categorie</button>
                <button className="lt-btn-primary" onClick={() => setShowNew(true)} data-testid="spese-new-btn"><Plus size={14} /> Nuova spesa</button>
            </div>

            <div className="grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }} data-testid="spese-counters">
                {counterCards.map((c) => (
                    <div key={c.key} className="lt-counter-card" style={{ cursor: "default" }} data-testid={`spese-counter-${c.key}`}>
                        <div className="lt-counter-card__value" style={{ fontSize: 24 }}>{c.value}</div>
                        <div className="lt-counter-card__label">{c.label}</div>
                    </div>
                ))}
            </div>

            {summary && summary.by && summary.by.length > 0 && (
                <div className="lt-card" style={{ padding: 20, marginBottom: 20 }} data-testid="spese-summary">
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
                        <h2 className="lt-h2">Riepilogo per categoria</h2>
                        <span className="lt-sub" style={{ fontSize: 13 }}>Periodo: {fmtDate(fromDate)} — {fmtDate(toDate)}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {summary.by.slice(0, 6).map((row, i) => {
                            const pct = summaryMax > 0 ? (row.amount_cents / summaryMax) * 100 : 0;
                            const label = row.key?.name || "Senza categoria";
                            return (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid={`spese-summary-row-${i}`}>
                                    <div style={{ minWidth: 180, color: "#181B20", fontSize: 14 }}>{label}</div>
                                    <div style={{ flex: 1 }}>
                                        <div className="lt-progress-bar"><div className="lt-progress-bar__fill" style={{ width: `${pct}%` }} /></div>
                                    </div>
                                    <div style={{ minWidth: 110, textAlign: "right", fontFamily: "'Archivo', sans-serif", fontWeight: 500, color: "#181B20" }}>{row.amount_eur}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="flex items-center" style={{ gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label className="lt-label">Da</label>
                    <input type="date" className="lt-input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ width: 170 }} data-testid="spese-from" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label className="lt-label">A</label>
                    <input type="date" className="lt-input" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ width: 170 }} data-testid="spese-to" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label className="lt-label">Preset</label>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="spese-presets">
                        <button className="lt-btn-secondary" onClick={() => applyPreset("month")} data-testid="spese-preset-month">Mese corrente</button>
                        <button className="lt-btn-secondary" onClick={() => applyPreset("prev_month")} data-testid="spese-preset-prev">Mese scorso</button>
                        <button className="lt-btn-secondary" onClick={() => applyPreset("year")} data-testid="spese-preset-year">Anno corrente</button>
                        <button className="lt-btn-secondary" onClick={() => applyPreset("12m")} data-testid="spese-preset-12m">Ultimi 12 mesi</button>
                    </div>
                </div>
            </div>
            <div className="flex items-center" style={{ gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                <input type="search" className="lt-input" placeholder="Cerca descrizione…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 220 }} data-testid="spese-search" />
                <select className="lt-select" value={catF} onChange={(e) => setCatF(e.target.value)} data-testid="spese-cat-filter">
                    <option value="">Tutte le categorie</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select className="lt-select" value={clientF} onChange={(e) => setClientF(e.target.value)} data-testid="spese-client-filter">
                    <option value="">Tutti i clienti</option>
                    <option value="null">— Senza cliente —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
            </div>

            <div className="lt-card" style={{ padding: "12px 20px", marginBottom: 12, display: "flex", alignItems: "baseline", gap: 10 }} data-testid="spese-total-bar">
                <span className="lt-label">Totale periodo</span>
                <span style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: 22, color: "#181B20" }} data-testid="spese-total-value">{fmtEur(totalAmount)}</span>
                <span className="lt-sub" style={{ fontSize: 13 }}>· {items.length} {items.length === 1 ? "spesa" : "spese"}</span>
            </div>

            <div className="lt-card" style={{ padding: 0 }}>
                <table className="lt-table" data-testid="spese-table">
                    <thead>
                        <tr>
                            <th style={{ width: 120 }}>Data</th>
                            <th>Descrizione</th>
                            <th style={{ width: 180 }}>Categoria</th>
                            <th style={{ width: 180 }}>Cliente</th>
                            <th style={{ width: 140, textAlign: "right" }}>Importo</th>
                            <th style={{ width: 60 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={6} style={{ padding: 24, color: "#8A8F88" }}>Nessuna spesa nel periodo selezionato.</td></tr>
                        ) : items.map((it) => {
                            const big = it.amount_cents >= 100000;
                            return (
                                <tr key={it.id} data-testid={`spese-row-${it.id}`} onClick={() => setEditing(it)} style={{ cursor: "pointer" }}>
                                    <td style={{ color: "#5F6560", fontSize: 13 }}>{fmtDate(it.date)}</td>
                                    <td>
                                        <div style={{ fontWeight: 500, color: "#181B20" }}>{it.description}</div>
                                        {it.notes && <div style={{ color: "#8A8F88", fontSize: 12, marginTop: 2 }}>{it.notes}</div>}
                                    </td>
                                    <td><span className="lt-chip-doc lt-chip-doc--altro">{it.category?.name || "—"}</span></td>
                                    <td style={{ color: "#5F6560", fontSize: 13 }}>{it.client?.company_name || <span style={{ color: "#8A8F88" }}>—</span>}</td>
                                    <td style={{ textAlign: "right", fontFamily: "'Archivo', sans-serif", fontWeight: big ? 600 : 500, color: "#181B20" }}>{it.amount_eur}</td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <KebabMenu item={it} onEdit={() => setEditing(it)} onDelete={() => setConfirmDel({ id: it.id, description: it.description })} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showNew && (
                <div className="lt-modal-backdrop" onClick={() => setShowNew(false)}>
                    <div className="lt-modal" onClick={(e) => e.stopPropagation()} data-testid="spesa-new-modal">
                        <div className="lt-modal__head"><h2 className="lt-modal__title">Nuova spesa</h2><button className="lt-icon-btn" onClick={() => setShowNew(false)}><X size={16} /></button></div>
                        <SpesaForm categories={cats} clients={clients} onSave={create} onClose={() => setShowNew(false)} saving={saving} />
                    </div>
                </div>
            )}
            {editing && (
                <div className="lt-modal-backdrop" onClick={() => setEditing(null)}>
                    <div className="lt-modal" onClick={(e) => e.stopPropagation()} data-testid="spesa-edit-modal">
                        <div className="lt-modal__head"><h2 className="lt-modal__title">Modifica spesa</h2><button className="lt-icon-btn" onClick={() => setEditing(null)}><X size={16} /></button></div>
                        <SpesaForm initial={editing} categories={cats} clients={clients} onSave={saveEdit} onClose={() => setEditing(null)} saving={saving} />
                    </div>
                </div>
            )}
            {confirmDel && (
                <div className="lt-modal-backdrop" onClick={() => setConfirmDel(null)}>
                    <div className="lt-modal lt-modal--small" onClick={(e) => e.stopPropagation()}>
                        <div className="lt-confirm">
                            <h3 className="lt-confirm__title">Eliminare la spesa?</h3>
                            <p className="lt-confirm__sub">«{confirmDel.description}» verrà rimossa definitivamente.</p>
                            <div className="lt-form-actions">
                                <div style={{ flex: 1 }} />
                                <button className="lt-btn-secondary" onClick={() => setConfirmDel(null)}>Annulla</button>
                                <button className="lt-btn-danger" onClick={doDelete}>Elimina</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showCats && <CategoriesModal categories={cats} onClose={() => setShowCats(false)} onChange={loadCats} />}
        </div>
    );
}
